import React, { useState, useEffect } from 'react';
import { Edit2, Key, Check } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Toast, { ToastMessage } from '../common/Toast';
import { userService } from '../../services/userService';
import { extractApiError } from '../../utils/apiError';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput } from '../../validation/ValidatedFields';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';

// ── EditUserModal ──────────────────────────────────────────────────────────────

export interface EditUserTarget {
  user_id: number;
  login_id?: string | null;
  first_name?: string;
  last_name?: string;
}

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  target: EditUserTarget | null;
  onSaved: () => void;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, onClose, target, onSaved }) => {
  const [loginId, setLoginId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const vh = useFieldValidation();
  const { allowed: canModify, loading: permLoading } = usePermission('ORG_USERS', 'view+modify');

  // View-first edit: the modal opens read-only; the footer shows an Edit
  // button that unlocks the fields in place and swaps the action to Update.
  const [isEditing, setIsEditing] = useState(false);
  const fieldsDisabled = !isEditing;

  useEffect(() => {
    if (isOpen && target) {
      setLoginId(target.login_id ?? '');
      setFirstName(target.first_name ?? '');
      setLastName(target.last_name ?? '');
      vh.reset();
      setIsEditing(false);
    }
  }, [isOpen, target]);

  const canSave = loginId.trim().length > 0 && firstName.trim().length > 0 && lastName.trim().length > 0;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
    if (!target || !canSave) return;
    // Format validation (engine 109) on the name fields — submit gate.
    const formatOk = vh.validateAll([
      { name: 'first_name', code: 109, value: firstName, required: true },
      { name: 'last_name', code: 109, value: lastName, required: true },
    ]);
    if (!formatOk) return;
    setSaving(true);
    try {
      await userService.updateUser(target.user_id, {
        login_id: loginId.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      setToast({ type: 'success', message: 'User updated successfully.' });
      setTimeout(() => {
        onSaved();
        onClose();
      }, 600);
    } catch (e: any) {
      setToast({ type: 'error', message: extractApiError(e, 'Failed to update user.') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen && !!target} onClose={onClose} size="sm">
        <Modal.Header><Edit2 size={16} style={{ display: 'inline', marginRight: 6 }} />Edit User</Modal.Header>
        <Modal.Body>
          <form id="edit-user-form" onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <FormControl.Input
              label="Login ID"
              required
              data-testid="INP_agency_user_edit_login"
              value={loginId}
              onChange={e => setLoginId(e.target.value)}
              placeholder="e.g. siva.prasad"
              disabled={fieldsDisabled}
            />
            <ValidatedInput
              vh={vh}
              name="first_name"
              code={109}
              required
              label="First Name"
              data-testid="INP_agency_user_edit_first"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              disabled={fieldsDisabled}
            />
            <ValidatedInput
              vh={vh}
              name="last_name"
              code={109}
              required
              label="Last Name"
              data-testid="INP_agency_user_edit_last"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              disabled={fieldsDisabled}
            />
          </form>
        </Modal.Body>
        <Modal.Footer>
          {!isEditing ? (
            <Button
              key="edit"
              type="button"
              variant="fill"
              icon={Edit2}
              data-testid="BTN_agency_user_edit_unlock"
              onClick={() => setIsEditing(true)}
              disabled={permLoading || !canModify}
              title={!canModify ? NOT_AUTHORISED_MSG : undefined}
            >
              Edit
            </Button>
          ) : (
            <Button
              key="update"
              type="submit"
              form="edit-user-form"
              variant="update"
              icon={Check}
              data-testid="BTN_agency_user_edit_save"
              disabled={permLoading || !canModify || !canSave || saving}
              title={!canModify ? NOT_AUTHORISED_MSG : undefined}
            >
              {saving ? 'Updating...' : 'Update'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

// ── ResetPasswordModal ────────────────────────────────────────────────────────

export interface ResetPasswordTarget {
  user_id: number;
  display_name?: string;
}

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  target: ResetPasswordTarget | null;
  onSaved?: () => void;
}

// ── ManageUserModal — single modal, two tabs (Edit · Reset Password) ────────────

type ManageTab = 'edit' | 'reset';

export interface ManageUserTarget {
  user_id: number;
  login_id?: string | null;
  first_name?: string;
  last_name?: string;
  display_name?: string;
}

interface ManageUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  target: ManageUserTarget | null;
  onSaved: () => void;
  /** Tab to open on. Defaults to 'edit'. */
  initialTab?: ManageTab;
}

export const ManageUserModal: React.FC<ManageUserModalProps> = ({ isOpen, onClose, target, onSaved, initialTab = 'edit' }) => {
  const [activeTab, setActiveTab] = useState<ManageTab>('edit');

  // ── Edit tab state ──────────────────────────────────────────────────────
  const [loginId, setLoginId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // ── Reset Password tab state ────────────────────────────────────────────
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [savingPw, setSavingPw] = useState(false);

  const [toast, setToast] = useState<ToastMessage | null>(null);
  const vh = useFieldValidation();
  const { allowed: canModify, loading: permLoading } = usePermission('ORG_USERS', 'view+modify');

  useEffect(() => {
    if (isOpen && target) {
      setActiveTab(initialTab);
      setLoginId(target.login_id ?? '');
      setFirstName(target.first_name ?? '');
      setLastName(target.last_name ?? '');
      setPassword('');
      setConfirm('');
      vh.reset();
    }
  }, [isOpen, target, initialTab]);

  const displayName =
    target?.display_name
    || `${target?.first_name || ''} ${target?.last_name || ''}`.trim()
    || target?.login_id
    || (target ? `User #${target.user_id}` : '');

  // ── Edit handlers ───────────────────────────────────────────────────────
  const canSaveEdit = loginId.trim().length > 0 && firstName.trim().length > 0 && lastName.trim().length > 0;

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
    if (!target || !canSaveEdit) return;
    // Format validation (engine 109) on the name fields — submit gate.
    const formatOk = vh.validateAll([
      { name: 'first_name', code: 109, value: firstName, required: true },
      { name: 'last_name', code: 109, value: lastName, required: true },
    ]);
    if (!formatOk) return;
    setSavingEdit(true);
    try {
      await userService.updateUser(target.user_id, {
        login_id: loginId.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      setToast({ type: 'success', message: 'User updated successfully.' });
      setTimeout(() => {
        onSaved();
        onClose();
      }, 600);
    } catch (e: any) {
      setToast({ type: 'error', message: extractApiError(e, 'Failed to update user.') });
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Reset Password handlers ─────────────────────────────────────────────
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSavePw = password.length >= 6 && password === confirm;

  const handleSavePw = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
    if (!target || !canSavePw) return;
    setSavingPw(true);
    try {
      await userService.updateUser(target.user_id, { password });
      setToast({ type: 'success', message: 'Password reset successfully.' });
      setTimeout(() => {
        onSaved();
        onClose();
      }, 600);
    } catch (e: any) {
      setToast({ type: 'error', message: extractApiError(e, 'Failed to reset password.') });
    } finally {
      setSavingPw(false);
    }
  };

  const tabs: { key: ManageTab; label: string; icon: React.ReactNode }[] = [
    { key: 'edit',  label: 'Edit',           icon: <Edit2 size={14} /> },
    { key: 'reset', label: 'Reset Password', icon: <Key size={14} /> },
  ];

  return (
    <>
      <Modal isOpen={isOpen && !!target} onClose={onClose} size="sm">
        <Modal.Header>
          {displayName
            ? <>Manage "<span className="item-highlight">{displayName}</span>"</>
            : 'Manage User'}
        </Modal.Header>

        <Modal.Body>
          <div className="app-modal-tabs">
            {tabs.map(({ key, label, icon }) => (
              <button
                key={key}
                type="button"
                data-testid={`TAB_manage_user_${key}`}
                onClick={() => setActiveTab(key)}
                className={`app-modal-tab${activeTab === key ? ' app-modal-tab--active' : ''}`}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{icon}{label}</span>
              </button>
            ))}
          </div>

          {/* ── Edit tab ───────────────────────────────────────────────── */}
          {activeTab === 'edit' && (
            <form id="manage-user-edit-form" onSubmit={handleSaveEdit} className="flex flex-col gap-y-4 pt-4">
              <FormControl.Input
                label="Login ID"
                required
                data-testid="INP_manage_user_edit_login"
                value={loginId}
                onChange={e => setLoginId(e.target.value)}
                placeholder="e.g. siva.prasad"
              />
              <ValidatedInput
                vh={vh}
                name="first_name"
                code={109}
                required
                label="First Name"
                data-testid="INP_manage_user_edit_first"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
              />
              <ValidatedInput
                vh={vh}
                name="last_name"
                code={109}
                required
                label="Last Name"
                data-testid="INP_manage_user_edit_last"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
              />
            </form>
          )}

          {/* ── Reset Password tab ─────────────────────────────────────── */}
          {activeTab === 'reset' && (
            <form id="manage-user-reset-form" onSubmit={handleSavePw} className="flex flex-col gap-y-4 pt-4">
              <FormControl.Input
                label="New Password"
                required
                type="password"
                data-testid="INP_manage_user_reset_pw"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Min. 6 characters"
              />
              <FormControl.Input
                label="Confirm Password"
                required
                type="password"
                data-testid="INP_manage_user_reset_pw_confirm"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                error={mismatch ? 'Passwords do not match.' : undefined}
              />
            </form>
          )}
        </Modal.Body>

        <Modal.Footer>
          {activeTab === 'edit' ? (
            <Button
              type="submit"
              form="manage-user-edit-form"
              variant="update"
              icon={Check}
              data-testid="BTN_manage_user_edit_save"
              disabled={permLoading || !canModify || !canSaveEdit || savingEdit}
              title={!canModify ? NOT_AUTHORISED_MSG : undefined}
            >
              Update
            </Button>
          ) : (
            <Button
              type="submit"
              form="manage-user-reset-form"
              variant="fill"
              icon={Key}
              data-testid="BTN_manage_user_reset_pw_save"
              disabled={permLoading || !canModify || !canSavePw || savingPw}
              title={!canModify ? NOT_AUTHORISED_MSG : undefined}
            >
              Reset Password
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ isOpen, onClose, target, onSaved }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const { allowed: canModify, loading: permLoading } = usePermission('ORG_USERS', 'view+modify');

  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setConfirm('');
    }
  }, [isOpen]);

  const mismatch = confirm.length > 0 && password !== confirm;
  const canSave = password.length >= 6 && password === confirm;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
    if (!target || !canSave) return;
    setSaving(true);
    try {
      await userService.updateUser(target.user_id, { password });
      setToast({ type: 'success', message: 'Password reset successfully.' });
      setTimeout(() => {
        onSaved?.();
        onClose();
      }, 600);
    } catch (e: any) {
      setToast({ type: 'error', message: extractApiError(e, 'Failed to reset password.') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen && !!target} onClose={onClose} size="sm">
        <Modal.Header><Key size={16} style={{ display: 'inline', marginRight: 6 }} />Reset Password</Modal.Header>
        <Modal.Body>
          {target?.display_name && (
            <p style={{ fontSize: 13, color: 'var(--text-muted, #6b7280)', marginBottom: 12 }}>
              Reset password for <strong>{target.display_name}</strong>
            </p>
          )}
          <form id="reset-pw-form" onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <FormControl.Input
              label="New Password"
              required
              type="password"
              data-testid="INP_agency_user_reset_pw"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Min. 6 characters"
            />
            <FormControl.Input
              label="Confirm Password"
              required
              type="password"
              data-testid="INP_agency_user_reset_pw_confirm"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              helper={mismatch ? 'Passwords do not match.' : undefined}
            />
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="submit"
            form="reset-pw-form"
            variant="fill"
            icon={Key}
            data-testid="BTN_agency_user_reset_pw_save"
            disabled={permLoading || !canModify || !canSave || saving}
            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
          >
            {saving ? 'Resetting...' : 'Reset Password'}
          </Button>
        </Modal.Footer>
      </Modal>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};
